/**
 * useAuth — Authentication service hook.
 * (v4.8 Spec — Section 2: User Roles, Access & Portfolio Visibility)
 *
 * Supports:
 *   1. Email/password sign-in & sign-up
 *   2. Magic link (passwordless email)
 *   3. Biometric (WebAuthn / passkeys — Face ID, Touch ID)
 *   4. Auto user_profiles creation on first login
 *   5. Password reset flow
 *
 * All auth through Supabase Auth. User profile created in
 * user_profiles table on first login via database trigger.
 *
 * Components use AuthContext for reactive auth state, this
 * hook for imperative auth operations.
 */

import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuditLog } from './useAuditLog';
import { createServiceError, fromSupabaseError } from '@/lib/errors';
import type { ServiceResult, UUID } from '@/types';
import type { Session, User } from '@supabase/supabase-js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface SignInWithPasswordParams {
  readonly email: string;
  readonly password: string;
}

interface SignUpParams {
  readonly email: string;
  readonly password: string;
  readonly fullName: string;
  readonly organisationId: UUID;
  readonly role?: 'manager' | 'coordinator' | 'viewer';
}

interface MagicLinkParams {
  readonly email: string;
  readonly redirectTo?: string;
}

interface ResetPasswordParams {
  readonly email: string;
  readonly redirectTo?: string;
}

interface UpdatePasswordParams {
  readonly newPassword: string;
}

interface AuthResult {
  readonly user: User;
  readonly session: Session;
}

interface UseAuthReturn {
  /** Email/password sign-in */
  readonly signInWithPassword: (params: SignInWithPasswordParams) => Promise<ServiceResult<AuthResult>>;
  /** Sign up — creates auth user + user_profiles record */
  readonly signUp: (params: SignUpParams) => Promise<ServiceResult<AuthResult>>;
  /** Magic link sign-in (passwordless) */
  readonly signInWithMagicLink: (params: MagicLinkParams) => Promise<ServiceResult<null>>;
  /** Biometric / passkey registration */
  readonly registerPasskey: () => Promise<ServiceResult<null>>;
  /** Biometric / passkey sign-in */
  readonly signInWithPasskey: () => Promise<ServiceResult<AuthResult>>;
  /** Password reset — sends reset email */
  readonly resetPassword: (params: ResetPasswordParams) => Promise<ServiceResult<null>>;
  /** Update password (after reset link click) */
  readonly updatePassword: (params: UpdatePasswordParams) => Promise<ServiceResult<null>>;
  /** Sign out — clears session */
  readonly signOut: () => Promise<ServiceResult<null>>;
  /** Get current session */
  readonly getSession: () => Promise<ServiceResult<Session | null>>;
  /** Get current user */
  readonly getUser: () => Promise<ServiceResult<User | null>>;
}

/** The app URL for redirects */
const APP_URL = import.meta.env['VITE_APP_URL'] as string | undefined ?? 'https://app.laceygroup.com.au';

export function useAuth(): UseAuthReturn {
  const { log: auditLog } = useAuditLog();

  // ─────────────────────────────────────────────────────────────────────
  // Email/password sign-in
  // ─────────────────────────────────────────────────────────────────────

  const signInWithPassword = useCallback(
    async (params: SignInWithPasswordParams): Promise<ServiceResult<AuthResult>> => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: params.email,
          password: params.password,
        });

        if (error) {
          return {
            data: null,
            error: createServiceError(
              error.status === 400 ? 'VALIDATION_ERROR' : 'PERMISSION_DENIED',
              mapAuthErrorMessage(error.message),
              { severity: 'warning' }
            ),
            loading: false,
          };
        }

        if (!data.user || !data.session) {
          return {
            data: null,
            error: createServiceError('UNKNOWN_ERROR', 'Sign-in returned no session'),
            loading: false,
          };
        }

        // Ensure user_profiles record exists
        await ensureUserProfile(data.user);

        // Update last login
        void supabase
          .from('user_profiles')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', data.user.id);

        return {
          data: { user: data.user, session: data.session },
          error: null,
          loading: false,
        };
      } catch (err) {
        return {
          data: null,
          error: createServiceError(
            'NETWORK_ERROR',
            err instanceof Error ? err.message : 'Sign-in failed',
            { retry: true }
          ),
          loading: false,
        };
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────
  // Sign up — new user registration
  // ─────────────────────────────────────────────────────────────────────

  const signUp = useCallback(
    async (params: SignUpParams): Promise<ServiceResult<AuthResult>> => {
      try {
        const { data, error } = await supabase.auth.signUp({
          email: params.email,
          password: params.password,
          options: {
            data: {
              full_name: params.fullName,
              organisation_id: params.organisationId,
              role: params.role ?? 'viewer',
            },
            emailRedirectTo: `${APP_URL}/auth/callback`,
          },
        });

        if (error) {
          return {
            data: null,
            error: createServiceError(
              'VALIDATION_ERROR',
              mapAuthErrorMessage(error.message),
              { severity: 'warning' }
            ),
            loading: false,
          };
        }

        if (!data.user) {
          return {
            data: null,
            error: createServiceError('UNKNOWN_ERROR', 'Sign-up returned no user'),
            loading: false,
          };
        }

        // Create user_profiles record immediately
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            id: data.user.id,
            organisation_id: params.organisationId,
            email: params.email,
            full_name: params.fullName,
            role: params.role ?? 'viewer',
          });

        if (profileError) {
          console.error('Failed to create user profile:', profileError.message);
        }

        // Audit log — new user created
        void auditLog({
          organisationId: params.organisationId,
          action: 'create',
          entityType: 'user',
          entityId: data.user.id,
          afterValue: {
            email: params.email,
            full_name: params.fullName,
            role: params.role ?? 'viewer',
          },
        });

        return {
          data: {
            user: data.user,
            session: data.session as Session,
          },
          error: null,
          loading: false,
        };
      } catch (err) {
        return {
          data: null,
          error: createServiceError(
            'NETWORK_ERROR',
            err instanceof Error ? err.message : 'Sign-up failed',
            { retry: true }
          ),
          loading: false,
        };
      }
    },
    [auditLog]
  );

  // ─────────────────────────────────────────────────────────────────────
  // Magic link (passwordless email)
  // ─────────────────────────────────────────────────────────────────────

  const signInWithMagicLink = useCallback(
    async (params: MagicLinkParams): Promise<ServiceResult<null>> => {
      try {
        const { error } = await supabase.auth.signInWithOtp({
          email: params.email,
          options: {
            emailRedirectTo: params.redirectTo ?? `${APP_URL}/auth/callback`,
          },
        });

        if (error) {
          return {
            data: null,
            error: createServiceError(
              'VALIDATION_ERROR',
              mapAuthErrorMessage(error.message),
              { severity: 'warning' }
            ),
            loading: false,
          };
        }

        return { data: null, error: null, loading: false };
      } catch (err) {
        return {
          data: null,
          error: createServiceError(
            'NETWORK_ERROR',
            err instanceof Error ? err.message : 'Magic link request failed',
            { retry: true }
          ),
          loading: false,
        };
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────
  // Biometric / Passkey (WebAuthn)
  // ─────────────────────────────────────────────────────────────────────

  const registerPasskey = useCallback(async (): Promise<ServiceResult<null>> => {
    try {
      // Check if WebAuthn is available
      if (!window.PublicKeyCredential) {
        return {
          data: null,
          error: createServiceError(
            'VALIDATION_ERROR',
            'Biometric authentication is not supported on this device'
          ),
          loading: false,
        };
      }

      // Check if platform authenticator (Face ID/Touch ID) is available
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!available) {
        return {
          data: null,
          error: createServiceError(
            'VALIDATION_ERROR',
            'No biometric authenticator found on this device'
          ),
          loading: false,
        };
      }

      // Call Supabase edge function to start WebAuthn registration
      const { data: challenge, error: challengeError } = await supabase.functions.invoke(
        'webauthn-register-challenge',
        { body: {} }
      );

      if (challengeError) {
        return {
          data: null,
          error: fromSupabaseError(challengeError),
          loading: false,
        };
      }

      // Create credential via browser WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: challenge as PublicKeyCredentialCreationOptions,
      });

      if (!credential) {
        return {
          data: null,
          error: createServiceError('UNKNOWN_ERROR', 'Passkey registration cancelled'),
          loading: false,
        };
      }

      // Verify credential with edge function
      const { error: verifyError } = await supabase.functions.invoke(
        'webauthn-register-verify',
        {
          body: {
            credential: serializeCredential(credential as PublicKeyCredential),
          },
        }
      );

      if (verifyError) {
        return {
          data: null,
          error: fromSupabaseError(verifyError),
          loading: false,
        };
      }

      return { data: null, error: null, loading: false };
    } catch (err) {
      return {
        data: null,
        error: createServiceError(
          'UNKNOWN_ERROR',
          err instanceof Error ? err.message : 'Passkey registration failed'
        ),
        loading: false,
      };
    }
  }, []);

  const signInWithPasskey = useCallback(async (): Promise<ServiceResult<AuthResult>> => {
    try {
      if (!window.PublicKeyCredential) {
        return {
          data: null,
          error: createServiceError(
            'VALIDATION_ERROR',
            'Biometric authentication is not supported on this device'
          ),
          loading: false,
        };
      }

      // Get authentication challenge
      const { data: challenge, error: challengeError } = await supabase.functions.invoke(
        'webauthn-auth-challenge',
        { body: {} }
      );

      if (challengeError) {
        return {
          data: null,
          error: fromSupabaseError(challengeError),
          loading: false,
        };
      }

      // Authenticate via browser WebAuthn API
      const assertion = await navigator.credentials.get({
        publicKey: challenge as PublicKeyCredentialRequestOptions,
      });

      if (!assertion) {
        return {
          data: null,
          error: createServiceError('UNKNOWN_ERROR', 'Biometric authentication cancelled'),
          loading: false,
        };
      }

      // Verify with edge function — returns a session
      const { data, error: verifyError } = await supabase.functions.invoke(
        'webauthn-auth-verify',
        {
          body: {
            assertion: serializeCredential(assertion as PublicKeyCredential),
          },
        }
      );

      if (verifyError || !data) {
        return {
          data: null,
          error: verifyError
            ? fromSupabaseError(verifyError)
            : createServiceError('UNKNOWN_ERROR', 'Biometric verification failed'),
          loading: false,
        };
      }

      // Set the session from the edge function response
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: (data as Record<string, string>)['access_token'] ?? '',
        refresh_token: (data as Record<string, string>)['refresh_token'] ?? '',
      });

      if (sessionError) {
        return {
          data: null,
          error: createServiceError('UNKNOWN_ERROR', sessionError.message),
          loading: false,
        };
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const { data: userData } = await supabase.auth.getUser();

      if (!sessionData.session || !userData.user) {
        return {
          data: null,
          error: createServiceError('UNKNOWN_ERROR', 'Failed to establish session after biometric auth'),
          loading: false,
        };
      }

      return {
        data: { user: userData.user, session: sessionData.session },
        error: null,
        loading: false,
      };
    } catch (err) {
      return {
        data: null,
        error: createServiceError(
          'UNKNOWN_ERROR',
          err instanceof Error ? err.message : 'Biometric sign-in failed'
        ),
        loading: false,
      };
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Password reset
  // ─────────────────────────────────────────────────────────────────────

  const resetPassword = useCallback(
    async (params: ResetPasswordParams): Promise<ServiceResult<null>> => {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(params.email, {
          redirectTo: params.redirectTo ?? `${APP_URL}/auth/reset-password`,
        });

        if (error) {
          return {
            data: null,
            error: createServiceError('VALIDATION_ERROR', mapAuthErrorMessage(error.message)),
            loading: false,
          };
        }

        return { data: null, error: null, loading: false };
      } catch (err) {
        return {
          data: null,
          error: createServiceError(
            'NETWORK_ERROR',
            err instanceof Error ? err.message : 'Password reset failed',
            { retry: true }
          ),
          loading: false,
        };
      }
    },
    []
  );

  const updatePassword = useCallback(
    async (params: UpdatePasswordParams): Promise<ServiceResult<null>> => {
      try {
        const { error } = await supabase.auth.updateUser({
          password: params.newPassword,
        });

        if (error) {
          return {
            data: null,
            error: createServiceError('VALIDATION_ERROR', mapAuthErrorMessage(error.message)),
            loading: false,
          };
        }

        return { data: null, error: null, loading: false };
      } catch (err) {
        return {
          data: null,
          error: createServiceError(
            'NETWORK_ERROR',
            err instanceof Error ? err.message : 'Password update failed',
            { retry: true }
          ),
          loading: false,
        };
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────
  // Sign out
  // ─────────────────────────────────────────────────────────────────────

  const signOut = useCallback(async (): Promise<ServiceResult<null>> => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return {
          data: null,
          error: fromSupabaseError(error),
          loading: false,
        };
      }

      return { data: null, error: null, loading: false };
    } catch (err) {
      return {
        data: null,
        error: createServiceError(
          'NETWORK_ERROR',
          err instanceof Error ? err.message : 'Sign-out failed'
        ),
        loading: false,
      };
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Session & user accessors
  // ─────────────────────────────────────────────────────────────────────

  const getSession = useCallback(async (): Promise<ServiceResult<Session | null>> => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        return { data: null, error: fromSupabaseError(error), loading: false };
      }
      return { data: data.session, error: null, loading: false };
    } catch (err) {
      return {
        data: null,
        error: createServiceError('NETWORK_ERROR', err instanceof Error ? err.message : 'Failed to get session'),
        loading: false,
      };
    }
  }, []);

  const getUser = useCallback(async (): Promise<ServiceResult<User | null>> => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) {
        return { data: null, error: fromSupabaseError(error), loading: false };
      }
      return { data: data.user, error: null, loading: false };
    } catch (err) {
      return {
        data: null,
        error: createServiceError('NETWORK_ERROR', err instanceof Error ? err.message : 'Failed to get user'),
        loading: false,
      };
    }
  }, []);

  return {
    signInWithPassword,
    signUp,
    signInWithMagicLink,
    registerPasskey,
    signInWithPasskey,
    resetPassword,
    updatePassword,
    signOut,
    getSession,
    getUser,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ensure a user_profiles record exists for this auth user.
 * Called on every sign-in — idempotent.
 */
async function ensureUserProfile(user: User): Promise<void> {
  try {
    const { data: existing } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('id', user.id)
      .single();

    if (existing) return;

    // Extract metadata from auth user
    const metadata = user.user_metadata as Record<string, unknown> | undefined;
    const fullName = (metadata?.['full_name'] as string | undefined) ?? user.email ?? 'Unknown';
    const organisationId = metadata?.['organisation_id'] as string | undefined;
    const role = (metadata?.['role'] as string | undefined) ?? 'viewer';

    if (!organisationId) {
      console.error('No organisation_id in user metadata — cannot create profile');
      return;
    }

    await supabase.from('user_profiles').insert({
      id: user.id,
      organisation_id: organisationId,
      email: user.email ?? '',
      full_name: fullName,
      role,
    });
  } catch {
    console.error('Failed to ensure user profile');
  }
}

/** Map Supabase auth error messages to user-friendly messages */
function mapAuthErrorMessage(message: string): string {
  const map: Record<string, string> = {
    'Invalid login credentials': 'Invalid email or password.',
    'Email not confirmed': 'Please check your email and confirm your account.',
    'User already registered': 'An account with this email already exists.',
    'Password should be at least 6 characters': 'Password must be at least 6 characters.',
    'Email rate limit exceeded': 'Too many attempts. Please try again in a few minutes.',
    'Invalid Refresh Token: Refresh Token Not Found': 'Your session has expired. Please sign in again.',
  };
  return map[message] ?? message;
}

/** Serialise a WebAuthn credential for transmission to edge function */
function serializeCredential(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse | AuthenticatorAssertionResponse;
  const result: Record<string, unknown> = {
    id: credential.id,
    rawId: arrayBufferToBase64(credential.rawId),
    type: credential.type,
  };

  if ('attestationObject' in response) {
    const attestation = response as AuthenticatorAttestationResponse;
    result['response'] = {
      clientDataJSON: arrayBufferToBase64(attestation.clientDataJSON),
      attestationObject: arrayBufferToBase64(attestation.attestationObject),
    };
  } else {
    const assertion = response as AuthenticatorAssertionResponse;
    result['response'] = {
      clientDataJSON: arrayBufferToBase64(assertion.clientDataJSON),
      authenticatorData: arrayBufferToBase64(assertion.authenticatorData),
      signature: arrayBufferToBase64(assertion.signature),
      userHandle: assertion.userHandle ? arrayBufferToBase64(assertion.userHandle) : null,
    };
  }

  return result;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}
